import { toCanvas } from 'html-to-image';

const DEVICE_W = 393;
const DEVICE_H = 852;
const SAFE_T = 59;
const SAFE_B = 34;
const OVERLAY_W = 472.093;
const OVERLAY_H = 930.233;
const SCREEN_RADIUS = 55;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function roundRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

async function waitForImages(doc) {
  const images = Array.from(doc.images || []);
  await Promise.all(
    images.map((image) => {
      if (typeof image.decode === 'function') {
        return image.decode().catch(() => {});
      }
      if (image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        image.onload = resolve;
        image.onerror = resolve;
      });
    })
  );
}

function loadDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = dataUrl;
  });
}

function captureViaGuest(iframe, exportScale) {
  const width = DEVICE_W;
  const height = DEVICE_H - SAFE_T - SAFE_B;
  return new Promise((resolve) => {
    const win = iframe && iframe.contentWindow;
    if (!win) {
      resolve(null);
      return;
    }
    let finished = false;
    const finish = (value) => {
      if (finished) return;
      finished = true;
      window.removeEventListener('message', onMessage);
      resolve(value);
    };
    const onMessage = (event) => {
      if (event.source !== win) return;
      if (!event.data || event.data.type !== 'export-capture-result') return;
      if (!event.data.dataUrl) {
        finish(null);
        return;
      }
      loadDataUrl(event.data.dataUrl)
        .then((image) => {
          const canvas = document.createElement('canvas');
          canvas.width = image.width;
          canvas.height = image.height;
          canvas.getContext('2d').drawImage(image, 0, 0);
          finish(canvas);
        })
        .catch(() => finish(null));
    };
    window.addEventListener('message', onMessage);
    try {
      win.postMessage(
        { type: 'export-capture', width, height, pixelRatio: exportScale },
        window.location.origin
      );
    } catch (_) {
      finish(null);
      return;
    }
    window.setTimeout(() => finish(null), 20000);
  });
}

async function captureIframe(iframe, exportScale) {
  const doc = iframe && iframe.contentDocument;
  const win = iframe && iframe.contentWindow;
  if (!doc || !win || !doc.body) return null;
  await waitForImages(doc);
  const fromGuest = await captureViaGuest(iframe, exportScale);
  if (fromGuest) return fromGuest;
  const width = DEVICE_W;
  const height = DEVICE_H - SAFE_T - SAFE_B;
  try {
    return await toCanvas(doc.documentElement, {
      width,
      height,
      canvasWidth: width * exportScale,
      canvasHeight: height * exportScale,
      pixelRatio: exportScale,
      cacheBust: false,
      style: {
        transform: 'none',
        width: `${width}px`,
        height: `${height}px`,
      },
    });
  } catch (_) {
    return null;
  }
}

export async function exportDeviceImage({
  iframe,
  overlaySrc,
  exportScale,
  surfaceTop,
  surfaceBottom,
}) {
  const overlay = await loadImage(overlaySrc);
  const overlayW = Math.round(OVERLAY_W * exportScale);
  const overlayH = Math.round(OVERLAY_H * exportScale);
  const phoneW = Math.round(DEVICE_W * exportScale);
  const phoneH = Math.round(DEVICE_H * exportScale);
  const pad = Math.round(overlayH * 0.08);
  const canvas = document.createElement('canvas');
  canvas.width = overlayW + pad * 2;
  canvas.height = overlayH + pad * 2;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const ox = pad;
  const oy = pad;
  const screenX = ox + (overlayW - phoneW) / 2;
  const screenY = oy + (overlayH - phoneH) / 2;
  const contentX = screenX;
  const contentY = screenY + SAFE_T * exportScale;
  const contentH = (DEVICE_H - SAFE_T - SAFE_B) * exportScale;

  ctx.save();
  roundRect(ctx, screenX, screenY, phoneW, phoneH, SCREEN_RADIUS * exportScale);
  ctx.clip();
  ctx.fillStyle = surfaceTop || '#ffffff';
  ctx.fillRect(screenX, screenY, phoneW, SAFE_T * exportScale);
  ctx.fillStyle = surfaceBottom || surfaceTop || '#ffffff';
  ctx.fillRect(
    screenX,
    screenY + phoneH - SAFE_B * exportScale,
    phoneW,
    SAFE_B * exportScale
  );
  ctx.fillStyle = surfaceTop || '#ffffff';
  ctx.fillRect(contentX, contentY, phoneW, contentH);

  const frame = await captureIframe(iframe, exportScale);
  if (frame) {
    ctx.drawImage(frame, contentX, contentY, phoneW, contentH);
  }
  ctx.restore();

  ctx.drawImage(overlay, ox, oy, overlayW, overlayH);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('export failed'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function copyBlob(blob) {
  if (navigator.clipboard && window.ClipboardItem) {
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ]);
    return;
  }
  throw new Error('clipboard unavailable');
}
